import React, { useEffect, useState, useCallback } from "react";
import {
  Stack, Typography, Paper, TextField, Button, Chip, Box,
  List, ListItem, ListItemText, CircularProgress, Divider,
} from "@mui/material";
import SyncIcon from "@mui/icons-material/SyncOutlined";
import AddIcon from "@mui/icons-material/AddOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ChecklistOutlinedIcon from "@mui/icons-material/ChecklistOutlined";
import { apiGet, apiPost, apiDelete, ApiError } from "../api/client";
import { useToast } from "../ui/Toaster";

export default function Tasks() {
  const { error: toastError, success: toastSuccess } = useToast();

  const [tasks, setTasks] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async (signal) => {
    setLoading(true);
    try {
      const js = await apiGet("/api/tasks", { signal });
      setTasks(Array.isArray(js?.tasks) ? js.tasks : []);
    } catch (e) {
      if (e?.name === "AbortError") return;
      toastError("Couldn't load tasks", e instanceof ApiError ? e.message : String(e));
      setTasks([]);
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [toastError]);

  useEffect(() => {
    const ctrl = new AbortController();
    load(ctrl.signal);
    return () => ctrl.abort();
  }, [load]);

  const addTask = async (e) => {
    e?.preventDefault?.();
    const val = text.trim();
    if (!val) return;
    setSaving(true);
    try {
      const js = await apiPost("/api/tasks", { text: val });
      if (js?.success) {
        setText("");
        await load();
      } else {
        toastError("Couldn't add task");
      }
    } catch (err) {
      toastError("Couldn't add task", err instanceof ApiError ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  const removeTask = async (id) => {
    if (id === undefined || id === null) return;
    try {
      const js = await apiDelete(`/api/tasks/${encodeURIComponent(id)}`);
      if (js?.success) {
        toastSuccess("Task removed");
        await load();
      }
    } catch (err) {
      toastError("Couldn't delete", err instanceof ApiError ? err.message : String(err));
    }
  };

  return (
    <Box>
      {/* Page header — matches OC's standard layout */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'flex-start' }}
        spacing={{ xs: 1.5, sm: 0 }}
        sx={{ mb: { xs: 2, md: 3.5 } }}
      >
        <Box>
          <Typography variant="h1" sx={{ mb: 0.5, fontSize: { xs: '1.85rem', md: '2.5rem' } }}>
            Tasks
          </Typography>
          <Typography sx={{ color: 'text.secondary', fontSize: 14 }}>
            {tasks.length} open task{tasks.length === 1 ? "" : "s"}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            startIcon={loading ? <CircularProgress size={15} color="inherit" /> : <SyncIcon />}
            variant="contained"
            onClick={() => load()}
            disabled={loading}
          >
            Refresh
          </Button>
        </Stack>
      </Stack>

      {/* Add task card */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Box component="form" onSubmit={addTask} sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
          <TextField
            placeholder="Add a new task…"
            value={text}
            onChange={(e) => setText(e.target.value)}
            size="small"
            autoFocus
            fullWidth
          />
          <Button
            type="submit"
            variant="contained"
            startIcon={saving ? <CircularProgress size={15} color="inherit" /> : <AddIcon />}
            disabled={saving || !text.trim()}
          >
            Add
          </Button>
        </Box>
      </Paper>

      {/* Tasks list */}
      <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
        {loading && tasks.length === 0 ? (
          <Stack direction="row" alignItems="center" justifyContent="center" gap={1} sx={{ py: 6, color: 'text.secondary' }}>
            <CircularProgress size={14} />
            <Typography variant="body2">Loading…</Typography>
          </Stack>
        ) : tasks.length === 0 ? (
          <Stack alignItems="center" gap={1} sx={{ py: 6, color: 'text.secondary' }}>
            <ChecklistOutlinedIcon fontSize="small" />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>No tasks</Typography>
            <Typography variant="caption">Quick reminders and to-dos go here.</Typography>
          </Stack>
        ) : (
          <List disablePadding>
            {tasks.map((t, i) => (
              <React.Fragment key={t.id ?? `task-${i}`}>
                {i > 0 && <Divider component="li" />}
                <ListItem
                  sx={{ px: 2.5, py: 1.5, alignItems: 'flex-start' }}
                  secondaryAction={
                    <Chip
                      icon={<DeleteOutlineIcon fontSize="small" />}
                      label="Delete"
                      size="small"
                      variant="outlined"
                      onClick={() => removeTask(t.id)}
                      sx={{ cursor: 'pointer' }}
                    />
                  }
                >
                  <ListItemText
                    primary={
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', pr: 6 }}>
                        {t.text}
                      </Typography>
                    }
                  />
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        )}
        {loading && tasks.length > 0 && (
          <Box sx={{
            position: 'absolute', top: 8, right: 12,
            display: 'inline-flex', alignItems: 'center', gap: 0.75,
            bgcolor: 'background.paper', border: 1, borderColor: 'divider',
            borderRadius: 1, px: 1, py: 0.25, fontSize: '0.7rem',
            color: 'text.secondary',
          }}>
            <CircularProgress size={12} /> Refreshing
          </Box>
        )}
      </Paper>
    </Box>
  );
}
